import React, {
  createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

export interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  permissions?: string[];
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function clearAuthStorage() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  delete axios.defaults.headers.common['Authorization'];
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const redirectingRef = useRef(false);

  const logout = useCallback((redirect = true) => {
    clearAuthStorage();
    setUser(null);
    if (redirect && !window.location.pathname.startsWith('/login')) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const forceLogoutToLogin = useCallback(() => {
    if (redirectingRef.current) return;
    redirectingRef.current = true;
    clearAuthStorage();
    setUser(null);
    const path = window.location.pathname;
    if (!path.startsWith('/login') && !path.startsWith('/register')) {
      navigate('/login', { replace: true });
    }
    // 允许后续再次触发（例如重新登录后又过期）
    setTimeout(() => { redirectingRef.current = false; }, 500);
  }, [navigate]);

  const login = useCallback((token: string, nextUser: User) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(nextUser));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(nextUser);
    redirectingRef.current = false;
  }, []);

  // 启动时校验 token；过期则清会话
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      if (!token || !userData) {
        if (!cancelled) setLoading(false);
        return;
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      try {
        const resp = await axios.get('/api/auth/verify');
        if (!cancelled) {
          const u = resp.data.user as User;
          setUser(u);
          localStorage.setItem('user', JSON.stringify(u));
        }
      } catch {
        if (!cancelled) {
          clearAuthStorage();
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // API 401（token 过期/无效）自动跳登录；忽略登录/注册本身的失败
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        const status = error.response?.status;
        const url = String(error.config?.url || '');
        const isAuthAttempt = /\/api\/auth\/(login|register)\b/.test(url);
        if (status === 401 && !isAuthAttempt) {
          forceLogoutToLogin();
        }
        return Promise.reject(error);
      },
    );
    return () => { axios.interceptors.response.eject(id); };
  }, [forceLogoutToLogin]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout: () => logout(true) }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
